import api from "@/services/api";
import type {
  AgingFaixa,
  CortePeriodo,
  ReguaEstagio,
  SpcStatus,
} from "@/pages/Financeiro/inadimplenciaUtils";

export interface InadimplenciaParcela {
  billId: string;
  installmentId?: string;
  installmentNumber?: number;
  clientKey: string;
  clientId?: string;
  clientName: string;
  clientDocument?: string;
  dueDate: string;
  originalAmount: number;
  balanceAmount: number;
  isPaid: boolean;
  costCenterId?: string;
  costCenterName?: string;
  brokerName?: string;
  daysOverdue: number;
  emAcordo: boolean;
  conditionType?: string;
}

export interface InadimplenciaClienteRow {
  clientKey: string;
  clientId?: string;
  clientName: string;
  clientDocument?: string;
  ativo: boolean;
  inadimplente: boolean;
  emAcordo: boolean;
  spcStatus: SpcStatus;
  spcConsultadoEm?: string;
  spcBaixaLimite?: string;
  acordoObservacao?: string;
  maxDaysOverdue: number;
  valorVencido: number;
  valorCarteira: number;
  faixaAtraso: AgingFaixa | null;
  regua: ReguaEstagio;
  brokerName?: string;
  costCenterId?: string;
  costCenterName?: string;
  prioridadeSpc: boolean;
}

export interface InadimplenciaQuebra {
  key: string;
  label: string;
  clientesAtivos: number;
  clientesInadimplentes: number;
  taxaClientes: number;
  carteira: number;
  vencido: number;
  taxaValor: number;
  qtdTotal?: number;
  qtdVencido?: number;
}

export interface InadimplenciaPontoMensal {
  anoMes: string;
  vencido: number;
  carteira: number;
  taxa: number;
  qtdVencido: number;
  qtdTotal: number;
}

export interface InadimplenciaValorCorte {
  vencido: number;
  carteira: number;
  taxa: number;
  qtdVencido?: number;
  qtdTotal?: number;
}

export interface InadimplenciaPayload {
  cutDate: string;
  startDate?: string;
  lookbackStart: string;
  lookaheadEnd: string;
  kpis: {
    clientesAtivos: number;
    clientesInadimplentes: number;
    taxaClientes: number;
    carteira: number;
    vencido: number;
    emAcordo: number;
    taxaValor: number;
    qtdTitulos?: number;
    qtdTitulosVencidos?: number;
  };
  cortes: Record<CortePeriodo, InadimplenciaValorCorte>;
  aging: Record<AgingFaixa, number>;
  porVendedor: InadimplenciaQuebra[];
  porLoja: InadimplenciaQuebra[];
  serieMensal?: InadimplenciaPontoMensal[];
  clientes: InadimplenciaClienteRow[];
  boletos: InadimplenciaParcela[];
  boletosCarteira?: InadimplenciaParcela[];
  fechamentos?: InadimplenciaFechamento[];
  ultimoFechamento?: InadimplenciaFechamento | null;
}

export interface InadimplenciaFechamento {
  anoMes: string;
  cutDate: string;
  capturedAt: string;
  frozen: boolean;
  kpis: InadimplenciaPayload["kpis"];
  cortes: Record<CortePeriodo, InadimplenciaValorCorte>;
  aging: Record<AgingFaixa, number>;
}

export interface InadimplenciaClienteLocal {
  clientKey: string;
  clientId?: string;
  clientName?: string;
  spcStatus: SpcStatus;
  spcConsultadoEm?: string;
  spcBaixaLimite?: string;
  acordoAtivo: boolean;
  acordoIniciadoEm?: string;
  acordoObservacao?: string;
  acordoQuebradoEm?: string;
  updatedAt: string;
}

export async function fetchInadimplencia(opts: {
  cutDate: string;
  startDate?: string;
  costCenterIds?: string[];
  refresh?: boolean;
  signal?: AbortSignal;
}): Promise<InadimplenciaPayload> {
  const params: Record<string, string> = { cutDate: opts.cutDate };
  if (opts.startDate) params.startDate = opts.startDate;
  if (opts.costCenterIds && opts.costCenterIds.length > 0) {
    params.costCenterIds = opts.costCenterIds.join(",");
  }
  if (opts.refresh) params.refresh = "1";
  const { data } = await api.get("/api/inadimplencia", { params, signal: opts.signal, timeout: 180_000 });
  if (!data?.success || !data.data) {
    throw new Error(data?.error || "Falha ao carregar inadimplência.");
  }
  return data.data as InadimplenciaPayload;
}

export async function patchInadimplenciaCliente(
  clientKey: string,
  patch: {
    clientId?: string;
    clientName?: string;
    spcStatus?: SpcStatus;
    marcarBaixaSpc?: boolean;
    acordoAtivo?: boolean;
    acordoObservacao?: string | null;
  },
): Promise<InadimplenciaClienteLocal> {
  const { data } = await api.patch(
    `/api/inadimplencia/clientes/${encodeURIComponent(clientKey)}`,
    patch,
  );
  if (!data?.success || !data.data) {
    throw new Error(data?.error || "Falha ao salvar o cliente.");
  }
  return data.data as InadimplenciaClienteLocal;
}
